import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContentText,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { Controller, Resolver, useForm } from 'react-hook-form';
import { ModifiedCryptoFormValues } from '../types';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback } from 'react';
import { AutocompleteExchange } from 'src/Common/components';
import { useMutation, useQueryClient } from 'react-query';
import { postCryptosModified } from 'src/Api';
import { CRYPTOS_MODIFIED } from 'src/Api/endpoints';
import { toast } from 'react-toastify';
import { FindCrypto } from 'src/Modules/Arbitrage/components/FindCrypto';

const formSchema = yup.object({
  exchanges: yup.array().required(),
  symbol: yup.string().required(),
  cryptoId: yup.string().required(),
  address: yup.string().optional()
});

type FormSchemaType = yup.InferType<typeof formSchema>;

const resolver: Resolver<FormSchemaType> = yupResolver(formSchema);

interface Props {
  onClose: () => void;
}

const ExchangeMapperCreateContent = ({ onClose }: Props) => {
  const queryClient = useQueryClient();
  const createMutation = useMutation((data: ModifiedCryptoFormValues) => {
    return postCryptosModified(data);
  });

  const form = useForm<FormSchemaType>({
    defaultValues: formSchema.cast({}) as FormSchemaType,
    resolver,
    reValidateMode: 'onSubmit',
    mode: 'onChange'
  });

  const onSubmit = useCallback(
    (formData: ModifiedCryptoFormValues) => {
      createMutation.mutateAsync(formData).then(() => {
        onClose();
        queryClient.invalidateQueries(CRYPTOS_MODIFIED);
        toast(`${formData.symbol} mapping has been created successfully`, {
          theme: 'dark',
          autoClose: 8000,
          pauseOnFocusLoss: false
        });
      });
    },
    [createMutation, queryClient, onClose]
  );

  return (
    <>
      <Typography
        variant="body1"
        style={{
          fontSize: '1rem',
          padding: '1rem 2rem',
          opacity: 0.7,
          borderBottom: '0.1px solid #393939'
        }}
      >
        Map symbol to crypto
      </Typography>

      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DialogContentText style={{ padding: '2rem' }}>
          <Stack spacing={2}>
            <TextField
              variant="outlined"
              type="text"
              size="small"
              label="Symbol"
              {...form.register('symbol')}
            />
            <Controller
              control={form.control}
              name="cryptoId"
              render={({ field: { onChange } }) => (
                <FindCrypto
                  onFindCrypto={crypto => {
                    onChange(crypto.id.toString());
                  }}
                  allCryptos
                  label="Target Crypto ID"
                  placeholder="Search by symbol or name..."
                />
              )}
            />
            <Controller
              control={form.control}
              name="exchanges"
              render={({ field: { onChange } }) => (
                <AutocompleteExchange
                  multiple
                  disableCloseOnSelect
                  onChange={(_: any, value: any) => {
                    onChange(
                      Array.isArray(value)
                        ? value?.map((v: any) => v.id)
                        : undefined
                    );
                  }}
                  inputLabel="Exchanges"
                />
              )}
            />
            <TextField
              variant="outlined"
              type="text"
              size="small"
              label="Address"
              {...form.register('address')}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContentText>

        <DialogActions
          style={{ padding: '2rem', borderTop: '0.1px solid #393939' }}
        >
          <Button
            disabled={!form.formState.isValid}
            variant="contained"
            color="primary"
            fullWidth
            type="submit"
          >
            {createMutation.isLoading ? (
              <CircularProgress size={20} />
            ) : (
              'Assign'
            )}
          </Button>
        </DialogActions>
      </form>
    </>
  );
};

export const ExchangeMapperDialog = ({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      {open && <ExchangeMapperCreateContent onClose={onClose} />}
    </Dialog>
  );
};
